import { ENV } from "~/ENV"
import { EntitlementCategory } from "~/graphql/generated/graphql"

/**
 * Manage Access stays in myLifeway; the Digital Library links out to it.
 *
 * The destination is self-sufficient — `containers/ManageAccessContainer.js`
 * fetches `/licenses` on mount and `ManageAccess.js` redirects a missing media
 * type to its default — so the page-level link needs no data from us at all
 * (FINDINGS.md gap 25). The per-row link needs a licence id, which the DCE graph
 * has no concept of but the commerce rows carry on every seat.
 */
const MANAGE_ACCESS_PATH = "/manage-access"

/**
 * MLC's own resolve-and-redirect page. `resourceRedirectQuery`
 * (`utils/pathHelpers.js:33`) points every Access, Play Now, Read eBook and
 * Download button at it, and `ExternalResourceRedirectPage` fetches
 * `/resources/{itemNumber}/locations` and replaces the location with the first
 * URL — including the error banner when a seat has no location.
 *
 * **It resolves in the browser, so it only works from inside myLifeway.**
 * `getResourceURLs` sends the signed-in user's token; a link followed in a cold
 * tab has none and lands on sign-in or the error banner. So this is no longer the
 * primary path — `accessTarget.ts` derives the destination from the seat's
 * distribution channel instead. This stays for the two cases that genuinely need
 * click-time resolution: downloads, whose presigned URL expires within the hour,
 * and channel codes this build does not recognise. See FINDINGS.md gap 32.
 */
const RESOURCE_REDIRECT_PATH = "/redirect"

/** MLC's own tab slugs — `constants.js#PATH_MANAGE_ACCESS_{SIMULCAST,EBOOKS,VIDEOS}`. */
const TAB_BY_CATEGORY: Partial<Record<EntitlementCategory, string>> = {
  [EntitlementCategory.Simulcast]: "simulcasts",
  [EntitlementCategory.Ebook]: "ebooks",
  [EntitlementCategory.Video]: "videos",
}

const origin = (): string => ENV.MLC_ORIGIN.replace(/\/+$/, "")

/**
 * The header link. No licence and no eligibility check needed — the destination
 * fetches its own `/licenses` and redirects a missing media type to its default.
 *
 * A tab is passed when we can tell which one has something in it, matching MLC's
 * own precedence (`containers/DigitalMediaRQ.js:87`: simulcasts, else ebooks,
 * else videos). Omitted, the link still resolves; MLC just picks for itself.
 */
export const manageAccessUrl = (tab?: string): string =>
  tab ? `${origin()}${MANAGE_ACCESS_PATH}/${tab}` : `${origin()}${MANAGE_ACCESS_PATH}`

/** MLC's tab precedence, over rows that actually have something to manage. */
export const preferredManageTab = (
  rows: readonly { readonly category: EntitlementCategory; readonly isManageable: boolean }[],
): string | undefined => {
  const manageableIn = (category: EntitlementCategory): boolean =>
    rows.some(row => row.category === category && row.isManageable)

  if (manageableIn(EntitlementCategory.Simulcast)) return TAB_BY_CATEGORY[EntitlementCategory.Simulcast]
  if (manageableIn(EntitlementCategory.Ebook)) return TAB_BY_CATEGORY[EntitlementCategory.Ebook]
  if (manageableIn(EntitlementCategory.Video)) return TAB_BY_CATEGORY[EntitlementCategory.Video]

  return undefined
}

/**
 * The per-row link, `/manage-access/{tab}/{licenceId}`.
 *
 * Undefined for a category MLC has no Manage Access tab for: Downloads,
 * Applications and Memberships have no per-row control there either, so linking
 * them to a tab they do not belong in would be worse than omitting the button.
 */
export const manageLicenseUrl = (category: EntitlementCategory, licenseId: string | undefined): string | undefined => {
  const tab = TAB_BY_CATEGORY[category]

  return tab && licenseId ? `${origin()}${MANAGE_ACCESS_PATH}/${tab}/${encodeURIComponent(licenseId)}` : undefined
}

/**
 * Where a commerce row clicks through to, by item number. Undefined without one —
 * the redirect page sends a request with no `itemNumber` to the profile page,
 * which would read as a broken card.
 */
export const resourceRedirectUrl = (itemNumber: string | null | undefined): string | undefined =>
  itemNumber ? `${origin()}${RESOURCE_REDIRECT_PATH}?itemNumber=${encodeURIComponent(itemNumber)}` : undefined

export interface ManageableLicense {
  readonly isOwner?: boolean | null
  readonly isManager?: boolean | null
  readonly seatCount?: number | null
  readonly isPermanentSeat?: boolean | null
}

/**
 * MLC's `isManageable` (`utils/media.js:11`), field for field: manage rights,
 * plus either seats to hand out or an OBS seat that is transferable even at one
 * seat.
 *
 * The expiry check is MLC's `isExpirableMediaManagable` and is applied to every
 * category here, not just videos and simulcasts. MLC gates ebooks on plain
 * `isManageable`, so an expired ebook licence still offers Manage Access there
 * (`Tables/EbooksRQ.js:103`) — FINDINGS.md gap 25 reads that as an oversight
 * rather than intent, and this is the one MLC behaviour the POC deliberately
 * does not copy. Flip `expired` handling here if product says otherwise.
 */
export const canManageAccess = (
  license: ManageableLicense | null | undefined,
  channelCodes: readonly string[] | null | undefined,
  isExpired: boolean,
): boolean => {
  if (!license || isExpired) {
    return false
  }

  const hasRights = Boolean(license.isManager || license.isOwner)
  const hasSeatsToShare = (license.seatCount ?? 0) > 1
  const isTransferableObs = (channelCodes ?? []).includes("OBS") && license.isPermanentSeat === false

  return hasRights && (hasSeatsToShare || isTransferableObs)
}

/**
 * Whether the header link has anything behind it — MLC's
 * `hasLicenseWithMultiSeats` (`utils/media.js:8`), which gates its own header
 * button. Without it the link still resolves, it just lands on an empty page:
 * the cosmetic wart gap 25 describes.
 */
export const hasManageableLicense = (rows: readonly { readonly license?: ManageableLicense | null }[]): boolean =>
  rows.some(row => (row.license?.seatCount ?? 0) > 1)
